import moment from 'moment';

import { checkIfEdited } from '~/legacy-components/LegacyForms';

import { RowData } from '../useEquipmentTab';

import {
  EquipListType,
  deleteEquipment,
  postEquipmentData,
  putEquipmentData,
  putEquipmentList,
} from '.';

const convertDateString = (date: string) => {
  return moment(date).format('YYYY-MM-DD');
};

const newEquipmentObjectData = (eq: RowData) => {
  return {
    ...eq,
    bnNewCompanyItem: undefined,
    fEquipmentItem: eq.fEquipmentItem || '',
    fEquipmentReturnDate: eq.fEquipmentReturnDate
      ? convertDateString(eq.fEquipmentReturnDate)
      : '',
    fEquipmentInfo: eq.fEquipmentInfo || '',
    fEquipmentAttachmentId: '-1',
    fEquipmentDeliveryDate: eq.fEquipmentDeliveryDate
      ? convertDateString(eq.fEquipmentDeliveryDate)
      : '',
    fEquipmentAttachmentName: '',
    fEquipmentExpirationDate: eq.fEquipmentExpirationDate
      ? convertDateString(eq.fEquipmentExpirationDate)
      : '',
    fEquipmentCompEqFK: eq.fEquipmentCompEqFK
      ? eq.fEquipmentCompEqFK.toString()
      : '-1',
  };
};

const newEquipmentFormData = (eq: RowData) => {
  const formData = new FormData();
  formData.append('fEquipmentType', eq.fEquipmentType);
  formData.append('fEquipmentItem', eq.fEquipmentItem || '');
  formData.append('fEquipmentInvNumber', eq.fEquipmentInvNumber);
  formData.append(
    'fEquipmentReturnDate',
    eq.fEquipmentReturnDate ? convertDateString(eq.fEquipmentReturnDate) : '',
  );
  formData.append('fEquipmentInfo', eq.fEquipmentInfo || '');
  formData.append(
    'fEquipmentAttachmentId',
    eq.fEquipmentAttachmentId ? eq.fEquipmentAttachmentId.toString() : '-1',
  );
  formData.append(
    'fEquipmentDeliveryDate',
    eq.fEquipmentDeliveryDate
      ? convertDateString(eq.fEquipmentDeliveryDate)
      : '',
  );
  formData.append(
    'fEquipmentAttachmentName',
    eq.fEquipmentAttachmentName || '',
  );
  formData.append(
    'fEquipmentExpirationDate',
    eq.fEquipmentExpirationDate
      ? convertDateString(eq.fEquipmentExpirationDate)
      : '',
  );
  formData.append(
    'fEquipmentCompEqFK',
    eq.fEquipmentCompEqFK ? eq.fEquipmentCompEqFK.toString() : '-1',
  );
  if (eq.fEquipmentAttachment) {
    formData.append('file', eq.fEquipmentAttachment);
  }
  return formData;
};

const checkIfDataEdited = (
  equipmentId: number,
  allData: RowData[],
  defaultData: RowData[],
) => {
  const selectedEquipmentDefaultData = defaultData.find(
    (dd) => dd.fEquipmentId === equipmentId,
  );
  const selectedEquipmentData = allData.find(
    (dd) => dd.fEquipmentId === equipmentId,
  );
  if (!selectedEquipmentDefaultData || !selectedEquipmentData) {
    return false;
  }
  return checkIfEdited<RowData>(
    selectedEquipmentData,
    selectedEquipmentDefaultData,
  );
};

const postNewEquipment = async (
  eq: RowData,
  empId: number,
  equipList: EquipListType[],
) => {
  const eqList = equipList.find(
    (eql) => eql.fComEquipmentId === eq.fEquipmentCompEqFK,
  );
  if (eq.fEquipmentAttachmentName) {
    if (eq.fEquipmentAddUrl === 'new') {
      await postEquipmentData(
        newEquipmentFormData(eq),
        empId,
        'form',
        '/company',
      );
    } else {
      await postEquipmentData(newEquipmentFormData(eq), empId, 'form', '');
      if (eqList) {
        const updateEq = {
          ...eqList,
          fComEquipmentExpirationDate: eq.fEquipmentExpirationDate || '',
        };
        await putEquipmentList(updateEq, eqList.fComEquipmentId);
      }
    }
  } else {
    if (eq.fEquipmentAddUrl === 'new') {
      await postEquipmentData(
        newEquipmentObjectData(eq),
        empId,
        'json',
        '/company',
      );
    } else {
      await postEquipmentData(newEquipmentObjectData(eq), empId, 'json', '');
      if (eqList) {
        const updateEq = {
          ...eqList,
          fComEquipmentExpirationDate: eq.fEquipmentExpirationDate || '',
        };
        await putEquipmentList(updateEq, eqList.fComEquipmentId);
      }
    }
  }
};

const putNewEquipment = async (eq: RowData, empId: number, iEqId: number) => {
  if (eq.fEquipmentAttachmentName) {
    await putEquipmentData(newEquipmentFormData(eq), empId, iEqId, 'form');
  } else {
    await putEquipmentData(newEquipmentObjectData(eq), empId, iEqId, 'json');
  }
};

export const dataSaving = async (
  empId: number,
  deletedRows: number[],
  editedRows: number[],
  defaultData: RowData[],
  data: RowData[],
  equipList: EquipListType[],
) => {
  await Promise.all(
    deletedRows.map(async (row) => await deleteEquipment(empId, row)),
  );
  await Promise.all(
    data.map(async (row) =>
      row.bnNewCompanyItem !== undefined
        ? await postNewEquipment(row, empId, equipList)
        : undefined,
    ),
  );
  await Promise.all(
    editedRows.map(async (row) => {
      if (checkIfDataEdited(row, data, defaultData)) {
        const selectedEquipmentData = data.find(
          (dd) => dd.fEquipmentId === row,
        ) as RowData;
        return await putNewEquipment(selectedEquipmentData, empId, row);
      }
      return undefined;
    }),
  );
};
